import { Button, ColumnTable, Icon, Loading, Table } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { useRiskEvaluationSessionDataLoader } from '../loaders/StudentRiskEvaluationSessionDataLoader';
import {
  StudentRiskEvaluationEnrollmentState,
  StudentRiskEvaluationSessionEnrollment,
} from '../types';
import RiskLevelBadge from './RiskLevelBadge';
import RiskStateBadge from './RiskStateBadge';
import StudentRiskEvaluationSessionsEnrollmentHistoryModal, {
  StudentRiskEvaluationSessionsEnrollmentHistoryModalMethods,
} from './StudentRiskEvaluationSessionsEnrollmentHistoryModal';

const getMaxDate = (datesList: string[]) => {
  if (!datesList?.length) {
    return '';
  }

  const dates = datesList
    .filter((date) => !!date)
    .map((date) => new Date(date));

  dates.sort((a, b) => b.getTime() - a.getTime());

  return dates?.[0] ? dayjs(dates[0]).utc().format('DD/MM/YYYY') : '';
};

const StudentRiskEvaluationSessionTable = () => {
  const history = useHistory();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_RISK_EVALUATION_SESSIONS_STUDENTS,
  );

  const historyRef = useRef<StudentRiskEvaluationSessionsEnrollmentHistoryModalMethods>(
    null,
  );

  const {
    loading,
    query,
    result,
    updateQuery,
  } = useRiskEvaluationSessionDataLoader();

  const seeDetails = useCallback(
    (row: StudentRiskEvaluationSessionEnrollment) => {
      history.push(`/student-warnings/student-risk/${row.id}`);
    },
    [history],
  );

  const seeHistory = useCallback(
    (row: StudentRiskEvaluationSessionEnrollment) => {
      historyRef.current?.open(
        row?.studyPlanEnrollment?.riskSessionEnrollments || [],
      );
    },
    [],
  );

  const columns = useMemo<
    ColumnTable<StudentRiskEvaluationSessionEnrollment>[]
  >(
    () => [
      {
        columnName: 'name',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.name`),
        cellFormat: ({ row }) => (
          <div className="d-block text-center">
            <span className="d-block">{row.name}</span>
            <strong className="d-block">{row.identification}</strong>
          </div>
        ),
      },
      {
        columnName: 'promo',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.promo`),
        width: '150px',
      },
      {
        columnName: 'group',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.group`),
        width: '150px',
      },
      {
        columnName: 'calculatedRiskLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.riskLevel`),
        width: '120px',
        cellFormat: ({ value }) => <RiskLevelBadge riskLevel={value} />,
      },
      {
        columnName: 'definedRiskLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.definedRiskLevel`),
        width: '120px',
        cellFormat: ({ value }) =>
          value ? <RiskLevelBadge riskLevel={value} /> : '',
      },
      {
        columnName: 'state',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.state`),
        width: '90px',
        cellFormat: ({ value }) => (
          <RiskStateBadge
            state={value || StudentRiskEvaluationEnrollmentState.Active}
          />
        ),
      },
      {
        columnName: 'conditionality',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.conditionality`),
        width: '90px',
        cellFormat: ({ value }) =>
          t(`conditionality.${value ? 'true' : 'false'}`),
      },
      {
        columnName: 'history',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.history`),
        width: '90px',
        cellFormat: ({ row }) =>
          !!row?.studyPlanEnrollment?.riskSessionEnrollments?.length ? (
            <div>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => seeHistory(row)}
              >
                <Icon name="information" />
              </button>
            </div>
          ) : (
            <></>
          ),
      },
      {
        columnName: 'lastModification',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.lastModification`),
        width: '120px',
        cellFormat: ({ row }) => {
          const prevDates =
            row.studyPlanEnrollment.riskSessionEnrollments
              .filter((enrollment) => enrollment.id !== row.id)
              .map((enrollment) => String(enrollment.definedAt)) || [];

          if (row.definedAt) {
            return getMaxDate(prevDates.concat([String(row.definedAt)]));
          }

          return getMaxDate(prevDates);
        },
      },
      {
        columnName: 'action',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.action`),
        width: '150px',
        cellFormat: ({ row }) => (
          <div>
            <Button
              text={t(`table.actions.manage`)}
              size="sm"
              onClick={() => seeDetails(row)}
            />
          </div>
        ),
      },
    ],
    [t, seeDetails, seeHistory],
  );
  if (loading) {
    return (
      <>
        <Loading textBody={''} />
      </>
    );
  }

  if (!result?.success) {
    return <></>;
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        <Row>
          <Col xs={12} md={12}>
            <h4 className="fs-20 text-primary fw-700 w-100 mb-0">
              {t(`table.title`)}
            </h4>
            <p className="fs-16 w-100">{t(`table.description`)}</p>
          </Col>
        </Row>
        <Table
          data={result?.data?.data ?? []}
          columns={columns}
          noResultsText={t(`noResults`)}
          isLoadingResults={loading}
          pagination={{
            itemsPerPage: query.items_per_page,
            currentPage: query.page,
            totalItems: result.data.total,
            totalPages: result.data.total_pages,
            onChangePage: (nextPage) => {
              updateQuery({
                ...query,
                page: nextPage,
              });
            },
          }}
        />
      </div>
      <StudentRiskEvaluationSessionsEnrollmentHistoryModal ref={historyRef} />
    </>
  );
};

export default StudentRiskEvaluationSessionTable;
